<script setup lang="ts">
import { ref } from 'vue'
import CourseSelectionHost from '@/components/current-student-advanced-standing-form/course-selection/CourseSelectionHost.vue'
import DocumentationSection from '@/components/current-student-advanced-standing-form/documentation/DocumentationSection.vue'
import ExistingPrecedentsSection from '@/components/current-student-advanced-standing-form/existing-precedents/ExistingPrecedentsSection.vue'
import PreviousLearningSection from '@/components/current-student-advanced-standing-form/previous-learning/PreviousLearningSection.vue'
import PreviousSubjectsSection from '@/components/current-student-advanced-standing-form/previous-subjects/PreviousSubjectsSection.vue'
import StudentDetailsLoginHost from '@/components/current-student-advanced-standing-form/student-details/StudentDetailsLoginHost.vue'
import SubmissionHost from '@/components/current-student-advanced-standing-form/submission/SubmissionHost.vue'
import type { LegacyClassBinding } from '@/components/current-student-advanced-standing-form/shared/types'
import type { DocumentationRow } from '@/models/current-student-advanced-standing-form/documentation'
import type {
  ArticulationCondition,
  ArticulationTableRow,
  CourseRuleOutcomeRow,
  PrecedentOutcome,
  SubjectPrecedentTableRow,
} from '@/models/current-student-advanced-standing-form/precedents'
import type {
  ExternalCourse,
  ExternalSubject,
  PublicFormApiResponse,
  PublicFormVerificationStatus,
  StudentFormLoadData,
  SubjectRequest,
  WorkExperience,
} from '@/models/current-student-advanced-standing-form/api'

defineProps<{
  currentStepIndex: number
  secureData: string
  previousLearningExternalCourses: ExternalCourse[]
  workExperiences: WorkExperience[]
  externalCourses: ExternalCourse[]
  externalCoursesWithSubjects: ExternalCourse[]
  workExperienceMissingRequestedSubject: boolean
  missingWorkExperienceSubjectMessage: string
  handbookUrl: string
  showSpgTool: boolean
  documentationRows: DocumentationRow[]
  documentationErrorMessage: string
  handleAuthSessionError: (error: unknown, behavior: 'redirect' | 'reload') => boolean
  getErrorMessage: (error: unknown) => string
  externalCourseRowClass: (course: ExternalCourse) => LegacyClassBinding
  externalCourseSubjectRowspan: (course: ExternalCourse) => number
  workExperienceRowClass: (workExperience: WorkExperience) => LegacyClassBinding
  workExperienceSubjectRowspan: (workExperience: WorkExperience) => number
  isVerifiedStatus: (status: PublicFormVerificationStatus) => boolean
  canEditExternalCourse: (course: ExternalCourse) => boolean
  canEditWorkExperience: (workExperience: WorkExperience) => boolean
  emptyExternalSubjectsMessage: (course: ExternalCourse) => string
  externalSubjectRowClass: (subject: ExternalSubject) => LegacyClassBinding
  canEditExternalSubject: (subject: ExternalSubject) => boolean
  canAddExternalSubject: (course: ExternalCourse) => boolean
  courseSubjects: (course: ExternalCourse) => ExternalSubject[]
  subjectPrecedentRows: (subject: ExternalSubject) => SubjectPrecedentTableRow[]
  subjectDisplayCode: (subject: ExternalSubject) => string
  outcomePriorityClass: (outcome: PrecedentOutcome) => string
  articulationRows: (course: ExternalCourse) => ArticulationTableRow[]
  articulationConditions: (course: ExternalCourse) => ArticulationCondition[]
  courseRuleOutcomeRows: (course: ExternalCourse) => CourseRuleOutcomeRow[]
}>()

const emit = defineEmits<{
  'update-deploy-user': [response: PublicFormApiResponse<unknown>]
  'login-success': []
  'course-saved': []
  'add-external-course': []
  'edit-external-course': [course: ExternalCourse]
  'add-external-course-subject-request': [course: ExternalCourse]
  'delete-external-course-subject-request': [course: ExternalCourse, request: SubjectRequest]
  'add-work-experience': []
  'edit-work-experience': [workExperience: WorkExperience]
  'add-work-experience-subject-request': [workExperience: WorkExperience]
  'delete-work-experience-subject-request': [workExperience: WorkExperience, request: SubjectRequest]
  'edit-subject': [subject: ExternalSubject, course: ExternalCourse]
  'add-subject': [course: ExternalCourse]
  'open-subject-request': [subject: ExternalSubject]
  'remove-subject-request': [subject: ExternalSubject, request: SubjectRequest]
  'application-complete-changed': [complete: boolean]
}>()

const studentDetailsLoginHostRef = ref<InstanceType<typeof StudentDetailsLoginHost> | null>(null)
const courseSelectionHostRef = ref<InstanceType<typeof CourseSelectionHost> | null>(null)

function startPageLoad(): void {
  studentDetailsLoginHostRef.value?.startPageLoad()
}

function applyImpersonationQueryState(url: string): void {
  studentDetailsLoginHostRef.value?.applyImpersonationQueryState(url)
}

function applyMaintenanceState(data: StudentFormLoadData, user: PublicFormApiResponse<StudentFormLoadData>['user']): void {
  studentDetailsLoginHostRef.value?.applyMaintenanceState(data, user)
}

function showPageLoadError(message: string): void {
  studentDetailsLoginHostRef.value?.showPageLoadError(message)
}

function loginPendingOrHidden(): boolean {
  return studentDetailsLoginHostRef.value?.loginPendingOrHidden() ?? true
}

function isLoginValidated(): boolean {
  return studentDetailsLoginHostRef.value?.isLoginValidated() ?? false
}

async function processLogin(): Promise<void> {
  await studentDetailsLoginHostRef.value?.processLogin()
}

async function validateAndSaveCourseSelection(): Promise<void> {
  await courseSelectionHostRef.value?.validateAndSaveCourseSelection()
}

defineExpose({
  startPageLoad,
  applyImpersonationQueryState,
  applyMaintenanceState,
  showPageLoadError,
  loginPendingOrHidden,
  isLoginValidated,
  processLogin,
  validateAndSaveCourseSelection,
})
</script>

<template>
  <div class="content clearfix">
    <h2 id="step-1" class="title current">Student Details</h2>
    <section class="body current" aria-labelledby="step-1" v-show="currentStepIndex === 0">
      <StudentDetailsLoginHost
        ref="studentDetailsLoginHostRef"
        :handle-auth-session-error="handleAuthSessionError"
        :get-error-message="getErrorMessage"
        @update-deploy-user="emit('update-deploy-user', $event)"
        @login-success="emit('login-success')"
      />
    </section>

    <h2 id="step-2" class="title">La Trobe Course</h2>
    <section class="body" aria-labelledby="step-2" v-show="currentStepIndex === 1">
      <CourseSelectionHost
        ref="courseSelectionHostRef"
        :handle-auth-session-error="handleAuthSessionError"
        :get-error-message="getErrorMessage"
        @course-saved="emit('course-saved')"
      />
    </section>

    <h2 id="step-3" class="title">Previous Learning</h2>
    <section class="body" aria-labelledby="step-3" v-show="currentStepIndex === 2">
      <PreviousLearningSection
        :previous-learning-external-courses="previousLearningExternalCourses"
        :work-experiences="workExperiences"
        :external-course-row-class="externalCourseRowClass"
        :external-course-subject-rowspan="externalCourseSubjectRowspan"
        :work-experience-row-class="workExperienceRowClass"
        :work-experience-subject-rowspan="workExperienceSubjectRowspan"
        :is-verified-status="isVerifiedStatus"
        :can-edit-external-course="canEditExternalCourse"
        :can-edit-work-experience="canEditWorkExperience"
        :work-experience-missing-requested-subject="workExperienceMissingRequestedSubject"
        :missing-work-experience-subject-message="missingWorkExperienceSubjectMessage"
        @add-external-course="emit('add-external-course')"
        @edit-external-course="emit('edit-external-course', $event)"
        @add-external-course-subject-request="emit('add-external-course-subject-request', $event)"
        @delete-external-course-subject-request="(course, request) => emit('delete-external-course-subject-request', course, request)"
        @add-work-experience="emit('add-work-experience')"
        @edit-work-experience="emit('edit-work-experience', $event)"
        @add-work-experience-subject-request="emit('add-work-experience-subject-request', $event)"
        @delete-work-experience-subject-request="(workExperience, request) => emit('delete-work-experience-subject-request', workExperience, request)"
      />
    </section>

    <h2 id="step-4" class="title">Previous Subjects</h2>
    <section class="body" aria-labelledby="step-4" v-show="currentStepIndex === 3">
      <PreviousSubjectsSection
        :external-courses="externalCourses"
        :courses-with-subjects="externalCoursesWithSubjects"
        :empty-external-subjects-message="emptyExternalSubjectsMessage"
        :row-class="externalSubjectRowClass"
        :is-verified-status="isVerifiedStatus"
        :can-edit-external-subject="canEditExternalSubject"
        :can-add-external-subject="canAddExternalSubject"
        @edit-subject="(subject, course) => emit('edit-subject', subject, course)"
        @add-subject="emit('add-subject', $event)"
      />
    </section>

    <h2 id="step-5" class="title">Existing Precedents</h2>
    <section class="body" aria-labelledby="step-5" v-show="currentStepIndex === 4">
      <ExistingPrecedentsSection
        :external-courses="externalCourses"
        :handbook-url="handbookUrl"
        :show-spg-tool="showSpgTool"
        :course-subjects="courseSubjects"
        :subject-precedent-rows="subjectPrecedentRows"
        :subject-display-code="subjectDisplayCode"
        :outcome-priority-class="outcomePriorityClass"
        :articulation-rows="articulationRows"
        :articulation-conditions="articulationConditions"
        :course-rule-outcome-rows="courseRuleOutcomeRows"
        @open-subject-request="emit('open-subject-request', $event)"
        @remove-subject-request="(subject, request) => emit('remove-subject-request', subject, request)"
      />
    </section>

    <h2 id="step-6" class="title">Documentation</h2>
    <section class="body" aria-labelledby="step-6" v-show="currentStepIndex === 5">
      <DocumentationSection
        :rows="documentationRows"
        :error-message="documentationErrorMessage"
      />
    </section>

    <h2 id="step-7" class="title">Submission</h2>
    <section class="body" aria-labelledby="step-7" v-show="currentStepIndex === 6">
      <SubmissionHost
        :secure-data="secureData"
        :handle-auth-session-error="handleAuthSessionError"
        :get-error-message="getErrorMessage"
        @update-deploy-user="emit('update-deploy-user', $event)"
        @application-complete-changed="emit('application-complete-changed', $event)"
      />
    </section>
  </div>
</template>
